import React, {
    useContext,
    useEffect,
    useState, 
    useRef
} from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { getBasketData, refetchBasketData, NotificationContext, handleError } from "../router"

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";
import * as OPTIONS from "../option-fields";

import TickIcon from "../svg/tick";
import InfoCircle from "../svg/info-circle";
import WheelchairIcon from '../svg/wheelchair';
import PadlockIcon from '../svg/padlock';

let basket_ticket_ids = [];

export default function SeatButton(props) {

    const {notification, setNotification} = useContext(NotificationContext);

    const [status, setStatus] = useState(null);
    const [seatBand, setSeatBand] = useState(null);
    const [ticketTypeId, setTicketTypeId] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [seatInfo, setSeatInfo] = useState(null);
    const [lockType, setLockType] = useState(null);

    const { data: basketData } = getBasketData();

    // Used to pass to removeTicket
    const seatBasketId = useRef(null);

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", CONSTANTS.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(CONSTANTS.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const addTicket = useMutation({
        mutationFn: () => QUERIES.addTickets(1, ticketTypeId, props.seat.id),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            const prev_basket_ids = basket_ticket_ids;
            const new_basket_ticket_ids = data.map((ticket) => {
                return ticket.id
            });
            const newId = new_basket_ticket_ids.filter((basket_id) => !prev_basket_ids.length || !prev_basket_ids.includes(basket_id));

            basket_ticket_ids = new_basket_ticket_ids;
            seatBasketId.current = newId[0];
            refetchBasketData();
        },
    });

    const removeTicket = useMutation({
        mutationFn: () => QUERIES.removeTickets([seatBasketId.current]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            setIsSelected(false);
            refetchBasketData();
        },
    })
    
    useEffect(() => {
        // Get seat status
        if (!props.statusData || !props.statusData.status[props.seat.id]) return;
        
        // Map statuses to seats
        const statusMap = {
            A: "is-available",
            B: "is-in-basket",
            U: "is-unavailable",
            W: "is-wheelchair-locked",
            X: "is-locked"
        };
        const seat_status = statusMap[props.statusData.status[props.seat.id].status] || "";
        setStatus(seat_status);
        if (seat_status == "is-in-basket") {
            setIsSelected(true);
        } else {
            setIsSelected(false);
        }
                
        // Check for lock types
        if (seat_status == "is-wheelchair-locked") {
            setLockType("Wheelchair Seat");
        }

        if (props.statusData.status[props.seat.id].lockType) {
            const lock_type = props.statusData.status[props.seat.id].lockType;

            // Sometimes client's don't use the lock type correctly so this logic is here to ensure that Companion 
            // and Wheelchair lock types get the correct icons even if the status isn't correct
            if (lock_type.startsWith("E -") || lock_type.includes("Companion") || lock_type.includes("Escort")) {
                setLockType(props.statusData.status[props.seat.id].lockType);
            } else if (lock_type.includes("Wheelchair") || seat_status == "is-wheelchair-locked") {
                setLockType("Wheelchair Seat");
            } else {
                setLockType(props.statusData.status[props.seat.id].lockType);
            }
        }

        // Match the seat band id to the instance pricelist to find the band colour
        const seat_band = priceListData?.prices?.find((price) => {
            return price.priceBand.id == props.statusData.status[props.seat.id].priceBandId
                    && price.ticketType.name == CONSTANTS.DEFAULT_TICKET_TYPE
        });
        
        if (!seat_band || !seat_band.priceBand) return;
        // Set the ticket type ID to be used in the add/remove queries
        setTicketTypeId(seat_band.ticketType.id);
        
        // Set the band
        const band = CONSTANTS.DEFAULT_TICKET_PRICEBANDS?.find((band) => {
            return band.band == seat_band.priceBand.name
        });

        if (!band || !band.color) return;
        
        setSeatBand(band);
        // if the option is set to use price point colours then switch to that and update any colours that are set there
        if (OPTIONS.seatingPlanColourOptions.value?.innerHTML == "price_point") {
            // match the band to the colour set in the options
              const matchedColour = OPTIONS.seatingPlanColourOptions.colors?.find((item) => parseFloat(item.price) === parseFloat(band.amount)
                );
                if (matchedColour && matchedColour.colour) {
                    setSeatBand({
                        ...band,
                        color: matchedColour.colour
                    });
                } else {
                    setSeatBand(band); // fallback to existing band colour 
                }

        }


        // Find out if there is extra info
        if (props.statusData.info[props.seat.id]) {
            setSeatInfo(props.statusData.info[props.seat.id]);
        }

        if (seat_status == "is-available" || seat_status == "is-in-basket") {

            // Allows enabling of filter buttons in select-seats when data is available
            props.setEnabledFilters(prev => {
                const updated = [...prev];
                if (props.area && !updated.includes(props.area.id)) updated.push(props.area.id);
                
                const bandAmountStr = band.amount.toString(); // or `${band.amount}`
                if (!updated.includes(bandAmountStr)) updated.push(bandAmountStr);

                return updated;
              });
        }
        
    }, [props.statusData, priceListData]);
    
    useEffect(() => {

        // console.log("SeatButton useEffect", props.selectedBands, seatBand, props.selectedArea);
        // Toggle button filtering by price and area
        if  (   
                (props.selectedBands && seatBand && !props.selectedBands.includes(seatBand.band)) ||
                (props.selectedArea && props.selectedArea != props.area?.id)
            ) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
        
    }, [props.selectedBands, seatBand, props.selectedArea]);

    const handleOnClick = () => {
        
        if (isSelected) {
            // If selected is set from basket data on page load there will be no seat_basket_id in which case we need to get the id from the basket data
            if (!seatBasketId.current && basketData) {
                // Get basket id from basket data
                const basket_item = UTILS.getBasketTicketById(basketData, props.seat.id);
                const basket_id = basket_item ? basket_item.id : null;
                seatBasketId.current = basket_id;
            }
            if (!seatBasketId.current ) handleError("A seat id has not been provided.", setNotification);;
            
            removeTicket.mutateAsync();

            // TO DO: Set this to "is-info" if that is the original state
            setStatus("is-available");
            setIsSelected(false)
        }
        if (!isSelected) {
            addTicket.mutateAsync();
            setIsSelected(true);
        }
    }

    useEffect(() => {

        // If either mutation is pending disable the button in book-online.js with seatTicketIsPending
        if (addTicket.isPending || removeTicket.isPending) {
            props.setSeatTicketIsPending(true);
        } else {
            props.setSeatTicketIsPending(false);
        }
        
    }, [addTicket.isPending, removeTicket.isPending])

    if (!props.seat || !status) return null;

    if (status == "is-unavailable" && !isSelected && removeTicket.status != "pending") {
        return (
            <p className={`seating-plan__seat ${status}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px"}}>
                <span className='sr-text'>Seat {props.seat.name} is unavailable</span>
            </p>
        )
    }
    return (
        <button type="button" 
                className={`seating-plan__seat ${status} ${disabled ? 'disabled' : ''} ${props.seat.name}`}
                style={{top: props.seat.y+"px", left: props.seat.x+"px", backgroundColor: seatBand ? seatBand.color : "", color: seatBand ? seatBand.color : ""}}
                disabled={disabled || addTicket.isPending || removeTicket.isPending}
                aria-selected={isSelected && !removeTicket.isPending}
                onClick={() => {handleOnClick()} }>
                { isSelected && !removeTicket.isPending ? 
                    <>
                        <span className='sr-text'>Deselect seat {props.seat.name}</span>
                        <TickIcon /> 
                    </>
                    : 
                    <span className='sr-text'>Select seat {props.seat.name}</span> 
                }

                {(lockType || seatInfo) && (
                    <span className='seating-plan__more-info'>
                        {lockType == "Wheelchair Seat" ? (
                            <>
                                <strong className='sr-text'>{lockType}</strong>
                                <WheelchairIcon />
                            </>
                        ) : lockType ? (
                            <>
                                <strong className='sr-text'>{lockType}</strong>
                                <PadlockIcon />
                            </>
                        ) : seatInfo ? (
                            <>
                                <strong className='sr-text'>{seatInfo?.priceBandId?.replaceAll("+", " ")}</strong>
                                <InfoCircle />
                            </>
                        ) : null}
                    </span>
                )}

        </button>
    );
}